import {createFragmentContainer, graphql} from 'react-relay';
import {getCurrency} from '../util/BudgetUtils';
import Util from '../../../../../forecast-app/shared/util/util';
import ProjectUtil from '../../../../../forecast-app/shared/util/ProjectUtil';
import {hasSomePermission} from '../../../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../../Permissions';

const BudgetTotalsLoader = ({viewer, children}) => {
	const rows = [];
	const currency = getCurrency(viewer.company, viewer.project);
	const {financialNumbers, budget} = viewer.project;
	const hasRevenueAccess = hasSomePermission([
		PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION,
		PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE,
	]);
	const hasRevenueWithoutCostAccess = Util.hasRevenueWithoutCostAccess();
	const billable = ProjectUtil.projectTracksRevenue(viewer.project);
	let valueOfServiceData;
	if (billable) {
		valueOfServiceData = [
			{
				type: 'fixedPriceRevenue',
				value: hasRevenueAccess ? budget : null,
			},
			{
				type: 'baselineTimeAndExpenses',
				value: financialNumbers.baselineTimeAndExpenses,
			},
			{
				type: 'billablePlannedTimeAndExpenses',
				value: financialNumbers.billablePlannedTimeAndExpenses,
			},
			{
				type: 'billableActualTimeAndExpenses',
				value: financialNumbers.billableActualTimeAndExpenses,
			},
			{
				type: 'billableForecastTimeAndExpensesToComplete',
				value: financialNumbers.billableForecastTimeAndExpensesToComplete,
			},
			{
				type: 'billableTotalTimeAndExpensesAtCompletion',
				value: financialNumbers.billableTotalTimeAndExpensesAtCompletion,
			},
			{
				type: 'baselineVsBillableActualTimeAndExpenses',
				value: financialNumbers.baselineVsBillableActualTimeAndExpenses,
			},
			{
				type: 'baselineVsBillableTotalTimeAndExpensesAtCompletion',
				value: financialNumbers.baselineVsBillableTotalTimeAndExpensesAtCompletion,
			},
			{
				type: 'planVsBillableActualTimeAndExpenses',
				value: financialNumbers.planVsBillableActualTimeAndExpenses,
			},
			{
				type: 'planVsTotalBillableTimeAndExpensesAtCompletion',
				value: financialNumbers.planVsTotalBillableTimeAndExpensesAtCompletion,
			},
		];
		const revenueData = [
			{
				type: 'fixedPriceRevenue',
				value: hasRevenueAccess ? budget : null,
			},
			{
				type: 'baselineRevenue',
				value: financialNumbers.baselineRevenue,
			},
			{
				type: 'plannedRevenue',
				value: financialNumbers.plannedRevenue,
			},
			{
				type: 'totalActualRevenueRecognition',
				value: financialNumbers.totalActualRevenueRecognition,
			},
			{
				type: 'totalForecastRevenueToComplete',
				value: financialNumbers.totalForecastRevenueToComplete,
			},
			{
				type: 'totalRevenueRecognition',
				value: financialNumbers.totalRevenueRecognition,
			},
			{
				type: 'baselineVsActualRevenueRecognition',
				value: financialNumbers.baselineVsActualRevenueRecognition,
			},
			{
				type: 'baselineVsTotalRevenueAtCompletion',
				value: financialNumbers.baselineVsTotalRevenueAtCompletion,
			},
			{
				type: 'planVsActualRevenueRecognition',
				value: financialNumbers.planVsActualRevenueRecognition,
			},
			{
				type: 'planVsTotalRevenueAtCompletion',
				value: financialNumbers.planVsTotalRevenueAtCompletion,
			},
		];

		const revenueRow = {
			dataArray: revenueData,
			currency: currency,
			rowType: 'revenue',
		};
		rows.push(revenueRow);
	}

	// Non-billable projects
	if (!billable) {
		valueOfServiceData = [
			{
				type: 'fixedPriceRevenue',
				value: hasRevenueAccess ? budget : null,
			},
			{
				type: 'baselineTimeAndExpenses',
				value: financialNumbers.baselineTimeAndExpenses,
			},
			{
				type: 'allPlannedTimeAndExpenses',
				value: financialNumbers.allPlannedTimeAndExpenses,
			},
			{
				type: 'allActualTimeAndExpenses',
				value: financialNumbers.allActualTimeAndExpenses,
			},
			{
				type: 'allForecastTimeAndExpensesToComplete',
				value: financialNumbers.allForecastTimeAndExpensesToComplete,
			},
			{
				type: 'allTotalTimeAndExpensesAtCompletion',
				value: financialNumbers.allTotalTimeAndExpensesAtCompletion,
			},
			{
				type: 'baselineVsAllActualValueOfService',
				value: financialNumbers.baselineVsAllActualValueOfService,
			},
			{
				type: 'baselineVsAllProjectedValueOfService',
				value: financialNumbers.baselineVsAllProjectedValueOfService,
			},
			{
				type: 'estimatedVsAllActualValueOfService',
				value: financialNumbers.estimatedVsAllActualValueOfService,
			},
			{
				type: 'estimatedVsAllProjectedValueOfService',
				value: financialNumbers.estimatedVsAllProjectedValueOfService,
			},
		];
	}

	const costData = [
		{
			type: 'fixedPriceCost',
			value: financialNumbers.totalCostAtCompletion,
		},
		{
			type: 'baselineCost',
			value: financialNumbers.baselineCost,
		},
		{
			type: 'plannedCost',
			value: financialNumbers.plannedCost,
		},
		{
			type: 'actualCost',
			value: financialNumbers.actualCost,
		},
		{
			type: 'forecastCostToComplete',
			value: financialNumbers.forecastCostToComplete,
		},
		{
			type: 'totalCostAtCompletion',
			value: financialNumbers.totalCostAtCompletion,
		},
		{
			type: 'baselineVsActualCost',
			value: financialNumbers.baselineVsActualCost,
		},
		{
			type: 'baselineVsTotalCostAtCompletion',
			value: financialNumbers.baselineVsTotalCostAtCompletion,
		},
		{
			type: 'planVsActualCost',
			value: financialNumbers.planVsActualCost,
		},
		{
			type: 'planVsTotalCostAtCompletion',
			value: financialNumbers.planVsTotalCostAtCompletion,
		},
	];

	const costRow = {
		dataArray: costData,
		currency: currency,
		rowType: 'cost',
	};
	if (!hasRevenueWithoutCostAccess) {
		rows.push(costRow);
	}

	if (billable) {
		const profitData = [
			{
				type: 'fixedPriceProfit',
				value: hasRevenueAccess ? budget - financialNumbers.totalCostAtCompletion : null,
			},
			{
				type: 'baselineProfit',
				value: financialNumbers.baselineProfit,
			},
			{
				type: 'plannedRevenueProfit',
				value: financialNumbers.plannedRevenueProfit,
			},
			{
				type: 'actualRevenueProfit',
				value: financialNumbers.actualRevenueProfit,
			},
			{
				type: 'forecastRevenueProfitToComplete',
				value: financialNumbers.forecastRevenueProfitToComplete,
			},
			{
				type: 'totalRevenueProfitAtCompletion',
				value: financialNumbers.totalRevenueProfitAtCompletion,
			},
			{
				type: 'baselineVsActualRevenueProfit',
				value: financialNumbers.baselineVsActualRevenueProfit,
			},
			{
				type: 'baselineVsTotalRevenueProfitAtCompletion',
				value: financialNumbers.baselineVsTotalRevenueProfitAtCompletion,
			},
			{
				type: 'planVsActualRevenueProfit',
				value: financialNumbers.planVsActualRevenueProfit,
			},
			{
				type: 'planVsTotalRevenueProfitAtCompletion',
				value: financialNumbers.planVsTotalRevenueProfitAtCompletion,
			},
		];

		const profitRow = {
			dataArray: profitData,
			currency: currency,
			rowType: 'profit',
		};
		if (!hasRevenueWithoutCostAccess) {
			rows.push(profitRow);
		}
	}

	if (billable) {
		const marginData = [
			{
				type: 'fixedPriceMargin',
				value: hasRevenueAccess ? (budget - financialNumbers.totalCostAtCompletion) / budget : null,
			},
			{
				type: 'baselineMargin',
				value: financialNumbers.baselineMargin,
			},
			{
				type: 'plannedRevenueMargin',
				value: financialNumbers.plannedRevenueMargin,
			},
			{
				type: 'actualRevenueMargin',
				value: financialNumbers.actualRevenueMargin,
			},
			{
				type: 'forecastRevenueMarginToComplete',
				value: financialNumbers.forecastRevenueMarginToComplete,
			},
			{
				type: 'totalRevenueMarginAtCompletion',
				value: financialNumbers.totalRevenueMarginAtCompletion,
			},
			{
				type: 'baselineVsActualRevenueMargin',
				value: financialNumbers.baselineVsActualRevenueMargin,
			},
			{
				type: 'baselineVsTotalRevenueMarginAtCompletion',
				value: financialNumbers.baselineVsTotalRevenueMarginAtCompletion,
			},
			{
				type: 'planVsActualRevenueMargin',
				value: financialNumbers.planVsActualRevenueMargin,
			},
			{
				type: 'planVsTotalRevenueMarginAtCompletion',
				value: financialNumbers.planVsTotalRevenueMarginAtCompletion,
			},
		];

		const marginRow = {
			dataArray: marginData,
			currency: currency,
			rowType: 'margin',
		};
		if (!hasRevenueWithoutCostAccess) {
			rows.push(marginRow);
		}
	}

	if (valueOfServiceData) {
		const valueOfServiceRow = {
			dataArray: valueOfServiceData,
			currency: currency,
			rowType: billable ? 'billableValueOfService' : 'valueOfService',
		};
		rows.push(valueOfServiceRow);
	}

	const result = {data: {rows: [...rows]}};

	return children(result);
};
export const budgetTotalsLoaderQuery = graphql`
	query BudgetTotalsLoader_Query(
		$projectId: ID!
		$startYear: Int
		$startMonth: Int
		$startDay: Int
		$endYear: Int
		$endMonth: Int
		$endDay: Int
	) {
		viewer {
			actualPersonId
			component(name: "project_budget_v3_totals_loader")
			...BudgetTotalsLoader_viewer
				@arguments(
					projectId: $projectId
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
				)
		}
	}
`;

export default createFragmentContainer(BudgetTotalsLoader, {
	viewer: graphql`
		fragment BudgetTotalsLoader_viewer on Viewer
		@argumentDefinitions(
			projectId: {type: "ID!"}
			startYear: {type: "Int"}
			startMonth: {type: "Int"}
			startDay: {type: "Int"}
			endYear: {type: "Int"}
			endMonth: {type: "Int"}
			endDay: {type: "Int"}
		) {
			id
			company {
				currency
			}
			project(internalId: $projectId) {
				# Fixed price v1
				budget

				rateCard {
					currency
				}
				billable
				useBaseline
				budgetType
				useFixedPriceForBaselineRevenue
				financialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
				) {
					# Revenue
					plannedRevenue
					totalActualRevenueRecognition
					totalForecastRevenueToComplete
					totalRevenueRecognition
					baselineVsActualRevenueRecognition
					baselineVsTotalRevenueAtCompletion
					planVsActualRevenueRecognition
					planVsTotalRevenueAtCompletion
					baselineRevenue
					baselineVsBillableActualTimeAndExpenses
					baselineVsAllActualValueOfService
					baselineVsBillableTotalTimeAndExpensesAtCompletion
					baselineVsAllProjectedValueOfService
					billableActualTimeAndExpenses
					allActualTimeAndExpenses
					billablePlannedTimeAndExpenses
					allPlannedTimeAndExpenses
					planVsBillableActualTimeAndExpenses
					planVsTotalBillableTimeAndExpensesAtCompletion
					billableForecastTimeAndExpensesToComplete
					allForecastTimeAndExpensesToComplete
					billableTotalTimeAndExpensesAtCompletion
					allTotalTimeAndExpensesAtCompletion
					baselineTimeAndExpenses

					# Cost
					baselineCost
					baselineVsActualCost
					baselineVsTotalCostAtCompletion
					actualCost
					plannedCost
					planVsActualCost
					planVsTotalCostAtCompletion
					forecastCostToComplete
					totalCostAtCompletion
					estimatedVsAllActualValueOfService
					estimatedVsAllProjectedValueOfService

					# Profit
					baselineProfit

					# Revenue Profit
					baselineVsActualRevenueProfit
					baselineVsTotalRevenueProfitAtCompletion
					actualRevenueProfit
					plannedRevenueProfit
					planVsActualRevenueProfit
					planVsTotalRevenueProfitAtCompletion
					forecastRevenueProfitToComplete
					totalRevenueProfitAtCompletion

					# Margin
					baselineMargin
					baselineVsActualRevenueMargin
					baselineVsTotalRevenueMarginAtCompletion
					planVsActualRevenueMargin
					planVsTotalRevenueMarginAtCompletion

					# Revenue Margin
					actualRevenueMargin
					plannedRevenueMargin
					forecastRevenueMarginToComplete
					totalRevenueMarginAtCompletion
				}
			}
		}
	`,
});
